import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { CounterColour, ProbabilityData, ProbabilityQuestionOptions, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { BagEvent, PairEvent, SpinnerEvent, eventName } from "../Files/Prompts/probabilityEvents.ts";
import { probabilityPrompts as en } from "../Files/Prompts/en/probability.ts";
import { probabilityPrompts as es } from "../Files/Prompts/es/probability.ts";
import { probabilityPrompts as fr } from "../Files/Prompts/fr/probability.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * Probability with spinners and a bag of counters. See ADR 0108.
 *
 * **The count comes first, the sentence second.** Each problem draws its
 * object, picks an event, and counts the outcomes the event covers (`favourable`)
 * out of all of them (`total`). The answer is that fraction in lowest terms,
 * `0` or `1`, printed from those two integers. The prompt only words the event
 * in the sheet's language, so a sheet and its key cannot disagree.
 *
 * **Answers that need simplifying are asked for on purpose.** A fifth-section
 * spinner can never reduce (3/5 is already 3/5), and an event picked at random
 * on a six-section one is reducible about as often as not. Each draw is
 * therefore asked to be reducible about three times in five (`REDUCIBLE_SHARE`),
 * and falls back to anything valid when the object has no such event. Writing
 * the fraction in lowest terms is the skill the instruction line asks for
 * (ADR 0108 §2), so it has to be practised.
 *
 * **A spinner's numbers repeat.** Each is drawn from a small pool of numbers,
 * so two sections often share a number and an event covers more than one
 * section; that is the whole of how a spinner makes one outcome likelier
 * (ADR 0108 §1).
 */

/** How often a draw is asked for an answer that is not yet in lowest terms. */
const REDUCIBLE_SHARE = 60;

/** The spinner sizes a single spinner is cut into; 5 is the one that can't reduce, so it is the rarest. */
const SPINNER_SECTIONS = [4, 6, 8, 5, 4, 6, 8];
/** A spinner in a pair is smaller, with fewer sections. */
const PAIR_SECTIONS = { min: 3, max: 6 };
/** The bag holds this many counters. */
const BAG_SIZE = { min: 5, max: 12 };
/** About one single-spinner problem in this many asks for an impossible or a certain event (ADR 0108 §2). */
const EXTREME_ONE_IN = 8;

const COLOUR_SLOTS: CounterColour[] = ['R', 'B', 'G', 'Y'];

function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b);
}

/** Whether `favourable / total` is not already in lowest terms. */
export function needsSimplifying(favourable: number, total: number): boolean {
    return favourable > 0 && favourable < total && gcd(favourable, total) > 1;
}

/** The answer as a student writes it: lowest terms, `0` or `1`. */
export function probabilityAnswer(favourable: number, total: number): string {
    if (favourable === 0) return "0";
    if (favourable === total) return "1";
    const divisor = gcd(favourable, total);
    return `${favourable / divisor}/${total / divisor}`;
}

function pick<T>(list: T[]): T {
    return list[getRandomInt(0, list.length - 1)];
}

function wantsReducible(): boolean {
    return getRandomInt(1, 100) <= REDUCIBLE_SHARE;
}

/**
 * A spinner's `sections` numbers, 1 to 9: a small pool of numbers (3 to 5, no
 * more than there are sections) with every one used once at least, the rest
 * drawn from the pool, in a random order.
 */
function drawSpinner(sections: number): number[] {
    const pool = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]).slice(0, Math.min(sections, getRandomInt(3, 5)));
    const labels = [...pool];
    while (labels.length < sections) {
        labels.push(pick(pool));
    }
    return shuffle(labels);
}

const countOf = (labels: number[], test: (n: number) => boolean) => labels.filter(test).length;

// ── one spinner ──────────────────────────────────────────────────────────

interface Candidate<E> {
    event: E;
    favourable: number;
}

/** Every ordinary event on this spinner, grouped by kind, each covering some sections but not all. */
function spinnerCandidates(labels: number[]): Candidate<SpinnerEvent>[][] {
    const total = labels.length;
    const present = Array.from(new Set(labels));
    const between = (candidates: Candidate<SpinnerEvent>[]) =>
        candidates.filter((c) => c.favourable > 0 && c.favourable < total);
    const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
    return [
        between(present.map((n) => ({ event: { kind: 'number', n }, favourable: countOf(labels, (l) => l === n) }))),
        between(present.map((n) => ({ event: { kind: 'not', n }, favourable: countOf(labels, (l) => l !== n) }))),
        between([
            { event: { kind: 'even' }, favourable: countOf(labels, (l) => l % 2 === 0) },
            { event: { kind: 'odd' }, favourable: countOf(labels, (l) => l % 2 === 1) },
        ]),
        between(range(1, 8).map((n) => ({ event: { kind: 'greater', n }, favourable: countOf(labels, (l) => l > n) }))),
        between(range(2, 9).map((n) => ({ event: { kind: 'less', n }, favourable: countOf(labels, (l) => l < n) }))),
    ].filter((kind) => kind.length > 0);
}

/**
 * An event that can't happen (answer 0) or must (answer 1), which is where 7.SP.C.5
 * puts probability's two ends (ADR 0108 §2): a number that isn't on the spinner,
 * more than the biggest, less than the smallest; or less than something above
 * the biggest, greater than something below the smallest.
 */
function extremeSpinnerEvent(labels: number[]): Candidate<SpinnerEvent> {
    const total = labels.length;
    const smallest = Math.min(...labels);
    const biggest = Math.max(...labels);
    if (getRandomInt(0, 1) === 0) {
        const impossible: SpinnerEvent[] = [
            { kind: 'number', n: pick([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].filter((n) => !labels.includes(n))) },
            { kind: 'greater', n: biggest },
        ];
        if (smallest >= 2) impossible.push({ kind: 'less', n: smallest });
        return { event: pick(impossible), favourable: 0 };
    }
    const certain: SpinnerEvent[] = [{ kind: 'less', n: getRandomInt(biggest + 1, 10) }];
    certain.push({ kind: 'greater', n: getRandomInt(0, smallest - 1) });
    return { event: pick(certain), favourable: total };
}

export function buildSpinnerProblem(key: number, language: PrintedLanguage = "en"): ProblemData {
    const t = PROMPTS[language];
    const labels = drawSpinner(pick(SPINNER_SECTIONS));
    const total = labels.length;

    let chosen: Candidate<SpinnerEvent>;
    if (getRandomInt(1, EXTREME_ONE_IN) === 1) {
        chosen = extremeSpinnerEvent(labels);
    } else {
        const kinds = shuffle(spinnerCandidates(labels));
        const ask = wantsReducible();
        const fits = (c: Candidate<SpinnerEvent>) => !ask || needsSimplifying(c.favourable, total);
        // The first kind (in a random order) that has an event of the sort asked for; with none, any.
        const kind = kinds.find((candidates) => candidates.some(fits)) ?? kinds[0];
        chosen = pick(kind.filter(fits).length > 0 ? kind.filter(fits) : kind);
    }

    return {
        key,
        numbers: [],
        operator: "",
        display: [t.oneSpinner(chosen.event), probabilityAnswer(chosen.favourable, total)],
        probabilityQuestion: ProbabilityQuestionOptions.OneSpinner,
        probability: {
            spinners: [labels],
            event: eventName(chosen.event),
            favourable: chosen.favourable,
            total,
        },
    };
}

// ── a bag of counters ────────────────────────────────────────────────────

/** How many counters of each colour, 2 to 4 colours, each at least one, 5 to 12 in all. */
function drawBag(): Record<CounterColour, number> {
    const total = getRandomInt(BAG_SIZE.min, BAG_SIZE.max);
    const colours = shuffle(COLOUR_SLOTS).slice(0, getRandomInt(2, 4));
    const counts = { R: 0, B: 0, G: 0, Y: 0 } as Record<CounterColour, number>;
    for (const colour of colours) counts[colour] = 1;
    for (let dealt = colours.length; dealt < total; dealt++) {
        counts[pick(colours)]++;
    }
    return counts;
}

function bagCandidate(counts: Record<CounterColour, number>): Candidate<BagEvent> {
    const total = COLOUR_SLOTS.reduce((sum, c) => sum + counts[c], 0);
    const present = COLOUR_SLOTS.filter((c) => counts[c] > 0);
    // "red or blue" covering the whole bag would be a certain event, which only the spinner asks for.
    const kind = pick(present.length > 2 ? ["colour", "not", "either"] : ["colour", "not"]);
    if (kind === "either") {
        const [a, b] = shuffle(present);
        return { event: { kind: "either", colours: [a, b] }, favourable: counts[a] + counts[b] };
    }
    const colour = pick(present);
    return {
        event: { kind: kind as "colour" | "not", colour },
        favourable: kind === "colour" ? counts[colour] : total - counts[colour],
    };
}

const bagTotal = (counts: Record<CounterColour, number>) => COLOUR_SLOTS.reduce((sum, c) => sum + counts[c], 0);

export function buildBagProblem(key: number, language: PrintedLanguage = "en"): ProblemData {
    const t = PROMPTS[language];
    const ask = wantsReducible();
    let counts = drawBag();
    let chosen = bagCandidate(counts);
    for (let attempt = 0; attempt < 8 && ask && !needsSimplifying(chosen.favourable, bagTotal(counts)); attempt++) {
        counts = drawBag();
        chosen = bagCandidate(counts);
    }
    const total = bagTotal(counts);

    // Counted by the letters this language prints, in the order the slots are listed.
    const printed: Record<string, number> = {};
    for (const slot of COLOUR_SLOTS) {
        if (counts[slot] > 0) printed[t.colours[slot].letter] = counts[slot];
    }

    return {
        key,
        numbers: [],
        operator: "",
        display: [t.bag(chosen.event), probabilityAnswer(chosen.favourable, total)],
        probabilityQuestion: ProbabilityQuestionOptions.BagOfCounters,
        probability: {
            counters: printed,
            counterKey: t.counterKey(),
            event: eventName(chosen.event),
            favourable: chosen.favourable,
            total,
        },
    };
}

// ── two spinners ─────────────────────────────────────────────────────────

/** The outcomes of the pair covered by an event: left section crossed with right section. */
function pairFavourable(left: number[], right: number[], event: PairEvent): number {
    switch (event.kind) {
        case "both":
            return countOf(left, (l) => l === event.left) * countOf(right, (r) => r === event.right);
        case "bothEven":
            return countOf(left, (l) => l % 2 === 0) * countOf(right, (r) => r % 2 === 0);
        case "same":
            return left.reduce((sum, l) => sum + countOf(right, (r) => r === l), 0);
    }
}

function drawPair(): number[][] {
    return [
        drawSpinner(getRandomInt(PAIR_SECTIONS.min, PAIR_SECTIONS.max)),
        drawSpinner(getRandomInt(PAIR_SECTIONS.min, PAIR_SECTIONS.max)),
    ];
}

/**
 * A pair of spinners and an event on them that is neither impossible nor certain,
 * or `null` if the draw has none (two spinners with no number in common asked
 * for "the same number on both").
 */
function pairCandidate(spinners: number[][]): Candidate<PairEvent> | null {
    const [left, right] = spinners;
    const total = left.length * right.length;
    const kind = pick(["both", "bothEven", "same"]);
    const event: PairEvent =
        kind === "both"
            ? { kind: "both", left: pick(left), right: pick(right) }
            : kind === "bothEven"
            ? { kind: "bothEven" }
            : { kind: "same" };
    const favourable = pairFavourable(left, right, event);
    return favourable > 0 && favourable < total ? { event, favourable } : null;
}

export function buildPairProblem(key: number, language: PrintedLanguage = "en"): ProblemData {
    const t = PROMPTS[language];
    const ask = wantsReducible();
    let spinners = drawPair();
    let chosen = pairCandidate(spinners);
    for (let attempt = 0; attempt < 30; attempt++) {
        const total = spinners[0].length * spinners[1].length;
        if (chosen && (!ask || needsSimplifying(chosen.favourable, total))) break;
        spinners = drawPair();
        chosen = pairCandidate(spinners);
    }
    // Rare: thirty draws and no event that is neither impossible nor certain.
    // "A [first] and a [second]" with numbers taken from the spinners can't be either, so it is the way out.
    if (!chosen) {
        const event: PairEvent = { kind: "both", left: spinners[0][0], right: spinners[1][0] };
        chosen = { event, favourable: pairFavourable(spinners[0], spinners[1], event) };
    }
    const total = spinners[0].length * spinners[1].length;

    return {
        key,
        numbers: [],
        operator: "",
        display: [t.twoSpinners(chosen.event), probabilityAnswer(chosen.favourable, total)],
        probabilityQuestion: ProbabilityQuestionOptions.TwoEvents,
        probability: { spinners, event: eventName(chosen.event), favourable: chosen.favourable, total },
    };
}

/** "How many different outcomes are there?": the two section counts, multiplied. */
export function buildOutcomesProblem(key: number, language: PrintedLanguage = "en"): ProblemData {
    const t = PROMPTS[language];
    const spinners = drawPair();
    const total = spinners[0].length * spinners[1].length;
    return {
        key,
        numbers: [],
        operator: "",
        display: [t.outcomes(), String(total)],
        probabilityQuestion: ProbabilityQuestionOptions.TwoEvents,
        probability: { spinners, event: "outcomes", total },
    };
}

// ── the sheet ────────────────────────────────────────────────────────────

type Choice = { kind: "spinner" | "bag" | "pair" | "outcomes"; slot: number };

/**
 * What a sheet deals. Two Events alone is two problems in three asking for a
 * probability and one asking for the count of outcomes (ADR 0108 §2), dealt as
 * three distinct choices so the rotation is exact rather than a coin toss per
 * problem. Mixed deals the three kinds evenly, and its two-spinner share asks
 * for the count one time in three.
 */
function choicesFor(question: ProbabilityQuestionOptions): Choice[] {
    switch (question) {
        case ProbabilityQuestionOptions.OneSpinner:
            return [{ kind: "spinner", slot: 0 }];
        case ProbabilityQuestionOptions.BagOfCounters:
            return [{ kind: "bag", slot: 0 }];
        case ProbabilityQuestionOptions.TwoEvents:
            return [
                { kind: "pair", slot: 0 },
                { kind: "pair", slot: 1 },
                { kind: "outcomes", slot: 2 },
            ];
        default:
            return [
                { kind: "spinner", slot: 0 },
                { kind: "bag", slot: 1 },
                { kind: "pair", slot: 2 },
            ];
    }
}

// The same object, with the same event, asked twice is the same problem.
function fingerprint(problem: ProblemData): string {
    const data = problem.probability as ProbabilityData;
    return JSON.stringify([data.spinners, data.counters, data.event]);
}

/** The line printed once above a question page's first problem, in the sheet's language. */
export function probabilityInstruction(language: PrintedLanguage): string {
    return PROMPTS[language].instruction();
}

/** A page-shaped set of probability problems. */
export function generateProbabilityPages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.ProbabilityQuestion ?? ProbabilityQuestionOptions.Mixed;
    const language = printedLanguageOf(settings);
    const mixed = question === ProbabilityQuestionOptions.Mixed;
    return generateProblemPages({
        settings,
        choices: choicesFor(question),
        build: (choice, key) => {
            switch (choice.kind) {
                case "spinner":
                    return buildSpinnerProblem(key, language);
                case "bag":
                    return buildBagProblem(key, language);
                case "outcomes":
                    return buildOutcomesProblem(key, language);
                default:
                    return mixed && getRandomInt(1, 3) === 1
                        ? buildOutcomesProblem(key, language)
                        : buildPairProblem(key, language);
            }
        },
        fingerprint,
    });
}
